/**
 * Supabase Client - Centralized Supabase initialization
 *
 * This is the single source of truth for Supabase client configuration.
 * Import this file anywhere you need to interact with Supabase.
 *
 * NOTE: The service role client has been moved to Supabase Edge Functions.
 * Student operations now go through Edge Functions instead of using
 * the service role key in the browser. See supabase/functions/ for details.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

export interface SupabaseConfig {
  url: string;
  anonKey: string;
}

/**
 * Get Supabase configuration from environment variables
 */
export function getSupabaseConfig(): SupabaseConfig {
  const url = import.meta.env?.VITE_SUPABASE_URL || '';
  const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

  return { url, anonKey };
}

/**
 * Get the Supabase project URL (safe to call before client initialisation)
 */
export function getSupabaseUrl(): string {
  return import.meta.env?.VITE_SUPABASE_URL || '';
}

/**
 * Check if Supabase is configured
 */
export function hasSupabaseConfig(): boolean {
  const { url, anonKey } = getSupabaseConfig();
  return Boolean(url && anonKey && url.startsWith('http'));
}

/**
 * Singleton Supabase client instance
 */
let supabaseInstance: SupabaseClient | null = null;

/**
 * Get or create Supabase client instance
 *
 * @returns Supabase client instance
 * @throws Error if Supabase is not configured
 */
export function getSupabaseClient(): SupabaseClient {
  if (supabaseInstance) {
    return supabaseInstance;
  }

  const { url, anonKey } = getSupabaseConfig();

  if (!hasSupabaseConfig()) {
    throw new Error(
      'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.'
    );
  }

  supabaseInstance = createClient(url, anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
    },
    db: {
      schema: 'public',
    },
  });

  return supabaseInstance;
}

/**
 * Initialize Supabase client (optional - client is lazy-loaded)
 * Use this if you want to initialize Supabase early in app lifecycle
 */
export function initSupabase(): SupabaseClient | null {
  if (!hasSupabaseConfig()) {
    console.warn('Supabase not configured. Using mock data mode.');
    return null;
  }

  return getSupabaseClient();
}

/**
 * Reset Supabase client (useful for testing)
 */
export function resetSupabaseClient(): void {
  supabaseInstance = null;
}

// Default export
export const supabase = hasSupabaseConfig() ? getSupabaseClient() : null;
